import store from "../../../redux/store";
import {
    setSelectedResources,
    setResources,
    setFilteredResources,
    setContextMenuPosition,
    setContextMenuStatus,
    FileManagerView,
    setFileManagerView,
    setNewFolderModalStatus,
    setLinkUploadModalStatus,
    setIsSingleSelect,
    setPublicLinkStatus,
    setPublicLinkFolderStatus,
    setQuota,
    setContextMenuItems,
    setRenameModalStatus,
    setInformationModalStatus,
    setTrashConfirmationModalStatus,
    setDownloadConfirmationModalStatus,
    setDeleteConfirmationModalStatus,
    setCutModalStatus,
    setPreviewModalStatus,
    setShareModalStatus,
    setAddToResources,
    setSort,
    setUpdateResources,
    setUnshareConfimrationModalStatus,
    setScrollId,
    setPagination,
    setDownloadModalStatus,
    setDefaultSelectedResourse,
    setVersioningModalStatus,
    setVersionResources,
    setNextUrl,
    setCollaboraModalStatus
} from "../../../redux/slices/fileManagerSlice";
import { CurrentPlanType, QuotaType, ResourceType } from "../../../interfaces/general";
import { SortOnChangeType } from "../sortDropdown/sortDropdown.interface";
import { downloadResource, syncFileManager } from "./fileManager.function";
import { authToken } from "../../storage/storage";
import { advancedResponseValidator, get } from "../../../scripts/api";
import PATH from "../../../urls/path";
import API from "../../../urls/api";
import { collaboraExtensions, getFileExtension } from "../../../scripts/helpers";

const dispatch = store.dispatch;

export function handleSetResources(resources: ResourceType[] | undefined) {
    dispatch(setResources(resources));
}

export function handleSetVersionResources(resources: ResourceType[] | undefined) {
    dispatch(setVersionResources(resources));
}

export function handleSetFilteredResources(resources: ResourceType[]) {
    dispatch(setFilteredResources(resources));
}

export function handleClearAllSelectedResources() {
    if (!store.getState().fileManager.previewModalStatus && !store.getState().fileManager.informationModalStatus) {
        handleSetIsSingleSelect(false);
        dispatch(setSelectedResources([]));
    }
}

export function handleSelectResources(resources: ResourceType[]) {
    dispatch(setSelectedResources(resources));
}

export function handleAddToResources(resource: ResourceType) {
    const fileManager = store.getState().fileManager;

    dispatch(setAddToResources(resource));
    handleSetDefaultSelectedItem([...fileManager.defaultSelectedResourse.item, resource.id.toString()]);
}

export function handleUpdateResources(resource: ResourceType) {
    dispatch(setUpdateResources(resource));
}

export function handleSetIsSingleSelect(status: boolean) {
    dispatch(setIsSingleSelect(status));
}

export function handleSetContextMenuPosition(position: { x: number; y: number }) {
    dispatch(setContextMenuPosition(position));
}

export function handleSetContextMenuStatus(status: boolean) {
    dispatch(setContextMenuStatus(status));
}

export function handleSetContextMenuItems(items: string[]) {
    dispatch(setContextMenuItems(items));
}

export function handleSetFileManagerView(view: FileManagerView) {
    localStorage.setItem("FILE_MANAGER_VIEW", JSON.stringify(view));
    dispatch(setFileManagerView(view));
}

export function handleSetNewFolderModalStatus(status: boolean) {
    dispatch(setNewFolderModalStatus(status));
    handleSetContextMenuStatus(false);
    if (!status) handleClearAllSelectedResources();
}

export function handleSetLinkUploadModalStatus(status: boolean) {
    dispatch(setLinkUploadModalStatus(status));
    handleSetContextMenuStatus(false);
    if (!status) handleClearAllSelectedResources();
}

export function handleSetRenameModalStatus(status: boolean) {
    dispatch(setRenameModalStatus(status));
    handleSetContextMenuStatus(false);
    if (!status) handleClearAllSelectedResources();
}

export function handleSetCutModalStatus(status: boolean) {
    const fileManager = store.getState().fileManager;
    dispatch(setCutModalStatus(status));
    handleSetContextMenuStatus(false);
    if (!status) syncFileManager(false);
    if (!status && !fileManager.previewModalStatus) handleClearAllSelectedResources();
}

export function handleSetInformationModalStatus(status: boolean) {
    dispatch(setInformationModalStatus(status));
    handleSetContextMenuStatus(false);
    if (!status) handleClearAllSelectedResources();
}

export function handleSetTrashConfirmationModalStatus(status: boolean) {
    dispatch(setTrashConfirmationModalStatus(status));
}

export function handleSetDeleteConfirmationModalStatus(status: boolean) {
    dispatch(setDeleteConfirmationModalStatus(status));
    if (!status) handleClearAllSelectedResources();
}

export function handleSetDownloadConfirmationModalStatus(status: boolean) {
    dispatch(setDownloadConfirmationModalStatus(status));
    if (!status) {
        handleClearAllSelectedResources();
    }
}

export function handleSetDownloadModalStatus(status: boolean) {
    dispatch(setDownloadModalStatus(status));
    if (!status) handleClearAllSelectedResources();
}

export function handleSetPreviewModalStatus(status: boolean, inInformationModal = false) {
    dispatch(setPreviewModalStatus(status));
    if (!status && !inInformationModal) handleClearAllSelectedResources();
}

export function handleSetShareModalStatus(status: boolean) {
    const fileManager = store.getState().fileManager;
    handleSetContextMenuStatus(false);
    dispatch(setShareModalStatus(status));
    if (!status && !fileManager.previewModalStatus) handleClearAllSelectedResources();
}

export function handleSetCollaboraModalStatus(status: boolean) {
    const fileManager = store.getState().fileManager;
    handleSetContextMenuStatus(false);
    dispatch(setCollaboraModalStatus(status));
}

export function handleSetUnshareConfimrationModalStatus(status: boolean) {
    handleSetContextMenuStatus(false);
    dispatch(setUnshareConfimrationModalStatus(status));
    if (!status) handleClearAllSelectedResources();
}

export function handleSetVersioningModalStatus(status: boolean) {
    const fileManager = store.getState().fileManager;
    handleSetContextMenuStatus(false);
    dispatch(setVersioningModalStatus(status));
    if (!status && !fileManager.previewModalStatus) handleClearAllSelectedResources();
}

export function handleSetPublicLink(status: boolean) {
    handleSetContextMenuStatus(false);
    dispatch(setPublicLinkStatus(status));
    if (!status) handleClearAllSelectedResources();
}

export function handleSetPublicLinkFolder(status: boolean) {
    handleSetContextMenuStatus(false);
    dispatch(setPublicLinkFolderStatus(status));
    if (!status) handleClearAllSelectedResources();
}

export function handleSetQuota(quota: QuotaType) {
    dispatch(setQuota(quota));
}

export function handleSetSort(sort: SortOnChangeType) {
    dispatch(setSort(sort));
}

export function handleSetScrollId(scrollId: string | undefined) {
    dispatch(setScrollId(scrollId));
}

export function handleSetNextUrl(nextUrl: string | undefined) {
    dispatch(setNextUrl(nextUrl));
}

export function handleSetPagination(pagination: { page: number; pageSize: number }) {
    dispatch(setPagination(pagination));
}

export function handleSetDefaultSelectedItem(resourcesId: string[]) {
    const fileManager = store.getState().fileManager;
    dispatch(
        setDefaultSelectedResourse({
            item: resourcesId,
            update: !fileManager.defaultSelectedResourse.update
        })
    );
}

export function handleDownloadTheResource() {
    if (localStorage.getItem("HAMRAHI_DOWNLOAD_ALERT")) {
        downloadResource();
    } else {
        handleSetDownloadModalStatus(true);
    }
}

export function handleClearDefaultSelectedItem() {
    const fileManager = store.getState().fileManager;
    dispatch(
        setDefaultSelectedResourse({
            item: [],
            update: fileManager.defaultSelectedResourse.update
        })
    );
}

export const collaboraExtensionsChecker = (resource?: ResourceType) => {
    const selected = store.getState().fileManager.selectedResources[0] || resource;
    return collaboraExtensions.includes(getFileExtension(selected.name)!);
};

export const collaboraOtherExtensionsChecker = (resource?: ResourceType) => {
    const selected = store.getState().fileManager.selectedResources[0] || resource;
    return ["txt", "pdf"].includes(getFileExtension(selected.name)!);
};

export const handleOpenCollabora = (resource: ResourceType) => {
    const versionGroup = resource.version_group;
    const refreshToken = authToken.get()?.refresh;

    if (versionGroup)
        get((data, status) => {
            if (advancedResponseValidator(data, status)) {
                const url = new URL(data.wopi_url);
                url.searchParams.append("access_token", refreshToken!);
                const searchParams = new URLSearchParams();
                searchParams.set("url", btoa(url.href));
                window.open(PATH.collabora + "?" + searchParams.toString());
            }
        }, API.collabora(versionGroup));
};
